import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { OnboardingState, OnboardingUserData, LearningAvatar } from '../types/onboarding';

const initialUserData: OnboardingUserData = {
  firstName: '',
  lastName: '',
  birthday: null,
  pronouns: '',
  learningAvatar: null,
  school: '',
  schoolId: '',
  grade: '',
  transcriptUrl: '',
  favoriteCourses: [],
  challengingCourses: [],
};

interface OnboardingStore extends OnboardingState {
  // Actions
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  markStepComplete: (step: number) => void;
  updateUserData: <K extends keyof OnboardingUserData>(key: K, value: OnboardingUserData[K]) => void;
  updateMultipleUserData: (data: Partial<OnboardingUserData>) => void;
  setError: (error: string | null) => void;
  setSubmitting: (isSubmitting: boolean) => void;
  resetOnboarding: () => void;
  canNavigateToStep: (step: number) => boolean;
}

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set, get) => ({
      // Initial state
      currentStep: 1,
      completedSteps: [],
      userData: initialUserData,
      validation: {},
      isSubmitting: false,
      error: null,

      // Actions
      setCurrentStep: (step) => set({ currentStep: step }),
      
      nextStep: () => {
        const { currentStep, markStepComplete } = get();
        markStepComplete(currentStep);
        set({ currentStep: currentStep + 1 });
      },
      
      previousStep: () => {
        const { currentStep } = get();
        if (currentStep > 1) {
          set({ currentStep: currentStep - 1 });
        }
      },
      
      markStepComplete: (step) => {
        const { completedSteps } = get();
        if (!completedSteps.includes(step)) {
          set({ completedSteps: [...completedSteps, step] });
        }
      },
      
      updateUserData: (key, value) => {
        set((state) => ({
          userData: {
            ...state.userData,
            [key]: value,
          },
        }));
      },
      
      updateMultipleUserData: (data) => {
        set((state) => ({
          userData: {
            ...state.userData,
            ...data,
          },
        }));
      },
      
      setError: (error) => set({ error }),
      
      setSubmitting: (isSubmitting) => set({ isSubmitting }),
      
      resetOnboarding: () => {
        set({
          currentStep: 1,
          completedSteps: [],
          userData: initialUserData,
          validation: {},
          isSubmitting: false,
          error: null,
        });
      },
      
      canNavigateToStep: (step) => {
        const { completedSteps } = get();
        // Can always go to step 1
        if (step === 1) return true;
        // Can go to a step if the previous step is completed
        return completedSteps.includes(step - 1);
      },
    }),
    {
      name: 'onboarding-storage', // name of the item in localStorage
      partialize: (state) => ({
        currentStep: state.currentStep,
        completedSteps: state.completedSteps,
        userData: state.userData,
      }), // Only persist these fields
    }
  )
);